import { AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function PropGroup({ title, children }) {
  return (
    <div className="space-y-2.5">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        {title}
      </p>
      {children}
    </div>
  );
}

export function PropRow({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs text-gray-500 w-20 shrink-0">{label}</Label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function PropInput({ label, value, onChange, type = "text", ...rest }) {
  return (
    <PropRow label={label}>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) =>
          onChange(type === "number" ? Number(e.target.value) : e.target.value)
        }
        className="h-7 text-xs"
        {...rest}
      />
    </PropRow>
  );
}

export function PropColor({ label, value, onChange }) {
  return (
    <PropRow label={label}>
      <div className="flex gap-1.5 items-center">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0.5"
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-7 text-xs font-mono"
        />
      </div>
    </PropRow>
  );
}

export function PropSelect({ label, value, onChange, options }) {
  return (
    <PropRow label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => {
            const val = typeof opt === "string" ? opt : opt.value;
            const lbl = typeof opt === "string" ? opt : opt.label;
            return (
              <SelectItem key={val} value={val} className="text-xs">
                {lbl}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </PropRow>
  );
}

export function PropAlign({ label, value, onChange }) {
  return (
    <PropRow label={label}>
      <div className="flex gap-0.5">
        {[
          { val: "left", Icon: AlignLeft },
          { val: "center", Icon: AlignCenter },
          { val: "right", Icon: AlignRight },
        ].map(({ val, Icon }) => (
          <Button
            key={val}
            size="icon"
            variant={value === val ? "default" : "outline"}
            className="h-7 w-7"
            onClick={() => onChange(val)}
          >
            <Icon className="w-3 h-3" />
          </Button>
        ))}
      </div>
    </PropRow>
  );
}

export function PropTextarea({ label, value, onChange, rows = 3 }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="text-xs resize-none"
      />
    </div>
  );
}

export function EjsBindingInput({ value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500 flex items-center gap-1">
        EJS Binding{" "}
        <span className="text-[9px] text-blue-400 font-mono">
          {"<%= ... %>"}
        </span>
      </Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. invoice.companyName"
        className="h-7 text-xs font-mono"
      />
    </div>
  );
}
